import { ChecklistMetricStatus } from '../../handoff-checklist-v2/handoff-checklist-v2.types';
import { VoiceAirMetricDefinition } from '../voice-air-metrics.types';
import { WEAVE_APP_URL } from 'apis/sso/sso-url-config';
import {
  CallRouteType,
  NodeType,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import {
  getCallRouteReadQueryOptions,
  getCallRoutesListQueryOptions,
  getPhoneTenantsQueryOptions,
} from '../voice-air-handoff.queries';
import { getParsedErrorMessage } from 'redux/axios';

export const voiceAiRoutingMetric: VoiceAirMetricDefinition = {
  id: 'voice_ai_routing',
  name: 'Voice AI Routing',
  isRequired: true,
  criteria: [
    {
      description:
        'Verify phone tree or routing settings forward calls to the AI Receptionist.',
      whereToFix: {
        label: 'Call Routes',
        url: `${WEAVE_APP_URL}/home/dashboard#settings/phone/call-routes`,
      },
      evaluate: async (ctx) => {
        try {
          if (!ctx.orgId) {
            return {
              status: ChecklistMetricStatus.BLOCKED,
              reason: 'org_id_missing',
              message: 'Organization ID is not available for this location.',
            };
          }

          // Fetch tenants under this org
          const tenantsResp = await ctx.queryClient.fetchQuery(
            getPhoneTenantsQueryOptions(ctx.orgId)
          );

          // Find the tenant that contains the current locationId
          const matchingTenant = tenantsResp.tenants?.find((t) =>
            t.locations?.some((loc) => loc.id === ctx.locationId)
          );

          const tenantId = matchingTenant?.id;

          if (!tenantId) {
            return {
              status: ChecklistMetricStatus.BLOCKED,
              reason: 'tenant_id_missing',
              message: 'Phone Tenant ID is not available for this location.',
            };
          }

          // Phase 1: List all call routes
          const response = await ctx.queryClient.fetchQuery(
            getCallRoutesListQueryOptions(tenantId)
          );

          const callRoutes = response?.callRoutes || [];

          if (callRoutes.length === 0) {
            return {
              status: ChecklistMetricStatus.BLOCKED,
              reason: 'ai_voice_routing_missing',
              message:
                'AI Voice is missing from all call routes. At least one call route must include an AI Voice (Weave Assistant) step.',
            };
          }

          // Prioritize routes with assigned phone numbers
          const prioritizedRoutes = callRoutes.sort((a, b) => {
            const lenA = a.phoneNumberIds?.length || 0;
            const lenB = b.phoneNumberIds?.length || 0;
            return lenB - lenA; // routes with more phone numbers first
          });

          // Phase 2: Read routes sequentially, short-circuit on first match
          for (const route of prioritizedRoutes) {
            const detail = await ctx.queryClient.fetchQuery(
              getCallRouteReadQueryOptions(route.callRouteId)
            );

            const nodes = detail?.nodes || [];
            // Find WEAVE_ASSISTANT node
            const assistantNode = nodes.find(
              (n) => n.type === NodeType.NODE_TYPE_WEAVE_ASSISTANT
            );

            if (assistantNode) {
              const expansion =
                assistantNode.callObject &&
                'weaveAssistantExpansion' in assistantNode.callObject
                  ? assistantNode.callObject.weaveAssistantExpansion
                  : undefined;
              const greetingScript = expansion?.greetingScript?.trim() || '';
              const placeholderScript = expansion?.placeholderScript?.trim() || '';

              const hasPhoneNumbers = (route.phoneNumberIds?.length || 0) >= 1;

              if (!placeholderScript) {
                return {
                  status: ChecklistMetricStatus.BLOCKED,
                  reason: 'ai_voice_placeholder_missing',
                  message:
                    'AI Voice node exists in a call route but the placeholder script is not configured.',
                };
              }

              if (!hasPhoneNumbers) {
                return {
                  status: ChecklistMetricStatus.BLOCKED,
                  reason: 'ai_voice_no_phone_numbers',
                  message:
                    'AI Voice node exists in a call route but no phone numbers are assigned to the route.',
                };
              }

              if (!greetingScript) {
                return {
                  status: ChecklistMetricStatus.WARNING,
                  reason: 'ai_voice_greeting_missing',
                  message:
                    'AI Voice node exists in a call route but the greeting script is not configured.',
                };
              }

              return {
                status: ChecklistMetricStatus.PASSED,
                reason: 'ai_voice_routing_configured',
                message:
                  'AI Voice is present in a call route with phone numbers assigned.',
              };
            }
          }

          return {
            status: ChecklistMetricStatus.BLOCKED,
            reason: 'ai_voice_routing_missing',
            message:
              'AI Voice is missing from all call routes. At least one call route must include an AI Voice (Weave Assistant) step.',
          };
        } catch (err) {
          return {
            status: ChecklistMetricStatus.BLOCKED,
            reason: 'api_request_failed',
            message:
              getParsedErrorMessage(err) ||
              'Failed to verify active call routes and phone tree configuration.',
          };
        }
      },
    },
  ],
};
